import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Filter, Download, Loader2, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { dashApi } from "../api";
import {
  PIPELINE_STAGES, STAGE_LABEL, STAGE_BADGE,
  formatFormType, formatDateTime, getDisplayName,
  LEAD_TYPES, LEAD_TYPE_LABEL, LEAD_TYPE_BADGE,
} from "../utils";

// Sample requests + quick-ship samples now flow through the form_submissions
// pipeline as first-class citizens (see routes/forms.py::create_sample_request
// mirror + scripts/backfill_sample_shadow_submissions.py). The canonical row
// still lives in `sample_requests`; we mirror a shadow for CRM visibility.
const FORM_TYPES = [
  "contact", "quote", "fast_quote", "sample_request", "quick_ship_sample",
  "roi_playbook", "custom_project", "schedule_call", "request_tester",
  "consultation_intake", "consultation_booking",
];

const PAGE_SIZE = 25;

const SortableHeader = ({ label, field, sortBy, sortDir, onSort, className = "" }) => {
  const active = sortBy === field;
  const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
  return (
    <th
      className={`text-left font-medium px-4 py-2.5 ${className}`}
      data-testid={`sort-${field}`}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 hover:text-slate-900 transition-colors ${active ? "text-slate-900" : ""}`}
      >
        {label}
        <Icon className={`h-3 w-3 ${active ? "" : "opacity-40"}`} />
      </button>
    </th>
  );
};

export default function SubmissionsListPage() {
  const [params, setParams] = useSearchParams();

  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "");
  const [formType, setFormType] = useState(params.get("form_type") || "");
  const [leadType, setLeadType] = useState(params.get("lead_type") || "");
  const [hotOnly, setHotOnly] = useState(params.get("hot") === "1");
  const [page, setPage] = useState(parseInt(params.get("page") || "1", 10));
  const [sortBy, setSortBy] = useState(params.get("sort_by") || "created_at");
  const [sortDir, setSortDir] = useState(params.get("sort_dir") || "desc");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const fetchList = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const skip = (page - 1) * PAGE_SIZE;
      const queryParams = { limit: PAGE_SIZE, skip };
      if (q) queryParams.q = q;
      if (status) queryParams.status = status;
      if (formType) queryParams.form_type = formType;
      if (leadType) queryParams.lead_type = leadType;
      if (hotOnly) queryParams.is_hot = "true";
      if (sortBy) queryParams.sort_by = sortBy;
      if (sortDir) queryParams.sort_dir = sortDir;
      const { data } = await dashApi.get("/submissions", { params: queryParams });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(e.message || "Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [q, status, formType, leadType, hotOnly, page, sortBy, sortDir]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Sync filters to URL
  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (status) next.status = status;
    if (formType) next.form_type = formType;
    if (leadType) next.lead_type = leadType;
    if (hotOnly) next.hot = "1";
    if (page > 1) next.page = String(page);
    if (sortBy !== "created_at") next.sort_by = sortBy;
    if (sortDir !== "desc") next.sort_dir = sortDir;
    setParams(next, { replace: true });
  }, [q, status, formType, leadType, hotOnly, page, sortBy, sortDir, setParams]);

  // Click a sortable column header. Toggles direction if same field, otherwise
  // switches to the new field with sensible default direction (asc for text,
  // desc for dates).
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir(field === "created_at" ? "desc" : "asc");
    }
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const exportCsv = async () => {
    // Same-origin to avoid www↔apex redirect issues on credentialed requests
    const url = new URL("/api/dashboard/export.csv", window.location.origin);
    if (status) url.searchParams.set("status", status);
    if (formType) url.searchParams.set("form_type", formType);
    try {
      const res = await fetch(url.toString(), { credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = res.headers.get("Content-Disposition")?.match(/filename="([^"]+)"/)?.[1] || "submissions.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setErr("CSV export failed: " + e.message);
    }
  };

  const clearFilters = () => {
    setQ(""); setStatus(""); setFormType(""); setLeadType(""); setHotOnly(false); setPage(1);
  };

  return (
    <div className="space-y-5" data-testid="submissions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Submissions</h1>
          <p className="text-sm text-slate-500 mt-1">{total.toLocaleString()} total leads</p>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="submissions-export-btn">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Filter bar */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-5 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="search"
              placeholder="Search name, email, company, message…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              className="pl-9"
              data-testid="submissions-search-input"
            />
          </div>
          <div className="md:col-span-3">
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="submissions-status-filter"
            >
              <option value="">All statuses</option>
              {PIPELINE_STAGES.map((s) => (
                <option key={s} value={s}>{STAGE_LABEL[s]}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <select
              value={formType}
              onChange={(e) => { setFormType(e.target.value); setPage(1); }}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="submissions-formtype-filter"
            >
              <option value="">All form types</option>
              {FORM_TYPES.map((t) => (
                <option key={t} value={t}>{formatFormType(t)}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <select
              value={leadType}
              onChange={(e) => { setLeadType(e.target.value); setPage(1); }}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="submissions-leadtype-filter"
            >
              <option value="">All lead types</option>
              {LEAD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-1 flex">
            <button
              type="button"
              onClick={() => { setHotOnly(!hotOnly); setPage(1); }}
              data-testid="submissions-hot-toggle"
              title="Show only hot leads"
              className={`w-full h-10 rounded-md border text-sm font-medium transition-colors ${
                hotOnly
                  ? "bg-rose-50 text-rose-700 border-rose-300"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              🔥 Hot
            </button>
          </div>
          <div className="md:col-span-1 flex">
            <Button
              type="button"
              variant="ghost"
              onClick={clearFilters}
              className="w-full h-10"
              data-testid="submissions-clear-filters-btn"
            >
              <Filter className="h-4 w-4 mr-1" /> Clear
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading submissions…
          </div>
        ) : err ? (
          <div className="p-6 text-rose-700 bg-rose-50">{err}</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="submissions-empty">
            No submissions match these filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="submissions-table">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <SortableHeader label="Name" field="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Company" field="company" className="hidden md:table-cell" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Form" field="formType" className="hidden lg:table-cell" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Submitted" field="created_at" className="hidden sm:table-cell" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((s) => (
                  <tr
                    key={s.id}
                    className={`hover:bg-slate-50 cursor-pointer transition-colors ${s.is_hot ? "bg-rose-50/40" : ""}`}
                    onClick={(e) => {
                      // allow click anywhere on row
                      e.currentTarget.querySelector("a[data-row-link]").click();
                    }}
                    data-testid={`submission-row-${s.id}`}
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/dashboard/submissions/${s.id}`}
                        data-row-link
                        className="font-medium text-slate-900 hover:text-blue-700 inline-flex items-center gap-1.5"
                      >
                        {s.is_hot && (
                          <span
                            className="text-rose-600"
                            title={s.hot_reason || "Hot lead"}
                            data-testid={`hot-badge-${s.id}`}
                          >🔥</span>
                        )}
                        {getDisplayName(s)}
                      </Link>
                      <div className="text-xs text-slate-500">{s.email}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-700">{s.company || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-slate-700">{formatFormType(s.formType)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1 items-start">
                        <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border ${STAGE_BADGE[s.status] || "bg-slate-100"}`}>
                          {STAGE_LABEL[s.status] || s.status || "new"}
                        </span>
                        <span
                          className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full border ${LEAD_TYPE_BADGE[s.lead_type || "new_prospect"]}`}
                          data-testid={`list-lead-type-${s.id}`}
                        >
                          {LEAD_TYPE_LABEL[s.lead_type || "new_prospect"]}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell text-slate-600 text-xs">
                      {formatDateTime(s.created_at || s.timestamp)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && items.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50/50">
            <div className="text-xs text-slate-600">
              Showing <strong>{(page - 1) * PAGE_SIZE + 1}</strong>–
              <strong>{Math.min(page * PAGE_SIZE, total)}</strong> of <strong>{total}</strong>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                data-testid="submissions-prev-page-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-slate-700">Page {page} / {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                data-testid="submissions-next-page-btn"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
